import { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { InputSelectField, Radio, RadioGroup, TextField } from '@tempmee/react';
import { numberOfOperatoriesOptions } from '~/components/registration/utils';
import { SectionEditDialog } from '~/components/shared/AccountArea';
import { OfficeInfoCollection } from '~/constants/ff.posthog';
import { NUMBER_OF_OPERATORIES_OPTIONS } from '~/constants/officeProfileOptions';
import {
  buildBasicInformationPatch,
  buildCredentialsPatch,
  buildProductivityPatch,
  isFilled,
  mergeXraySoftware,
  requiredOk,
  toSectionDefaults,
  useUpdateLocation,
  useUpdateLocationCredentials,
  useUpdateLocationProductivity,
} from '~/domain/LocationSettings';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import type { IUserCredentials } from '~/models/UserCredentials';
import { OfficeTypeEnum, toSelectOptions } from '~/utils/Enum';
import { useOfficeEducationOptions } from '../hooks/useOfficeEducationOptions';
import { RequireCredentials } from './RequireCredentials';
import type { ISectionModalProps } from './types';

const TITLE = 'Edit Basic Information';
const DATA_CY = 'edit-basic-information';

/** Figma wording over today's stored values. */
const OFFICE_TYPES = [
  {
    value: OfficeTypeEnum.PRIVATE_PRACTICE_INDEPENDENT,
    label: 'Independent Office Private Practice',
  },
  { value: OfficeTypeEnum.PRIVATE_PRACTICE_MULTI, label: 'Multi-Office Private Practice' },
  { value: OfficeTypeEnum.DSO, label: 'DSO' },
];

const OPERATORIES_OPTIONS = toSelectOptions(numberOfOperatoriesOptions);

type TBasicInformationFormProps = ISectionModalProps & { credentials: IUserCredentials };

const BasicInformationForm = ({
  officeId,
  office,
  credentials,
  onClose,
  onSaved,
}: TBasicInformationFormProps) => {
  const { specialties } = useOfficeEducationOptions();
  // GOTU-20014: with office-info collection on, Post shift counts a stored 'N/A' as missing and asks
  // again, so Location settings stops offering it, as Account Information does (GOTU-14830/15828).
  const hidesNotAnswered = useFeatureFlag(OfficeInfoCollection);
  const sections = useMemo(() => toSectionDefaults(office, credentials), [office, credentials]);
  const { control, handleSubmit, formState } = useForm({
    defaultValues: sections.basicInformation,
    mode: 'onChange',
  });
  const values = useWatch({ control });
  const { dirtyFields } = formState;

  const updateLocation = useUpdateLocation(officeId);
  const updateCredentials = useUpdateLocationCredentials(officeId);
  const updateProductivity = useUpdateLocationProductivity(officeId);

  const canSave =
    formState.isDirty &&
    requiredOk(dirtyFields.officeName, isFilled(values.officeName)) &&
    requiredOk(dirtyFields.officeType, isFilled(values.officeType)) &&
    requiredOk(dirtyFields.specialty, isFilled(values.specialty));

  // Each request goes out only when its part changed, and carries only what
  // changed; the credentials PATCH carries software and insurance unchanged so
  // a specialty edit drops neither.
  const save = handleSubmit(async (submitted) => {
    if (dirtyFields.officeName || dirtyFields.officeType) {
      await updateLocation.mutateAsync(
        buildBasicInformationPatch(
          {
            ...(dirtyFields.officeName && { officeName: submitted.officeName }),
            ...(dirtyFields.officeType && { officeType: submitted.officeType }),
          },
          office,
        ),
      );
    }
    if (dirtyFields.specialty) {
      await updateCredentials.mutateAsync(
        buildCredentialsPatch({
          specialty: submitted.specialty,
          softwares: mergeXraySoftware(
            sections.insuranceSoftware.softwares,
            sections.insuranceSoftware.xraySoftwares,
          ),
          insurances: sections.insuranceSoftware.insurances,
        }),
      );
    }
    if (dirtyFields.operatories) {
      await updateProductivity.mutateAsync(
        buildProductivityPatch(office, { operatories: submitted.operatories }),
      );
    }
    onSaved();
  });

  return (
    <SectionEditDialog
      title={TITLE}
      dataCy={DATA_CY}
      onClose={onClose}
      onSave={save}
      canSave={canSave}
    >
      <Controller
        name="officeName"
        control={control}
        render={({ field }) => (
          <TextField
            label="Location Name"
            error={!!dirtyFields.officeName && !isFilled(field.value)}
            data-cy="location-name"
            {...field}
          />
        )}
      />
      <TextField
        label="Location Code (Optional)"
        value={office.locationCode ?? '—'}
        disabled
        readOnly
        data-cy="location-code"
      />
      <Controller
        name="specialty"
        control={control}
        render={({ field }) => (
          <InputSelectField
            label="What is your area of primary practice?"
            options={specialties}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
      <Controller
        name="officeType"
        control={control}
        render={({ field }) => (
          <RadioGroup
            label="Are you a private practice or DSO?"
            value={field.value}
            onChange={field.onChange}
            orientation="vertical"
          >
            {OFFICE_TYPES.map((type) => (
              <Radio key={type.value} value={type.value} label={type.label} />
            ))}
          </RadioGroup>
        )}
      />
      <Controller
        name="operatories"
        control={control}
        render={({ field }) => (
          <InputSelectField
            label="Number of operatories"
            options={hidesNotAnswered ? NUMBER_OF_OPERATORIES_OPTIONS : OPERATORIES_OPTIONS}
            value={field.value ?? undefined}
            onChange={field.onChange}
          />
        )}
      />
    </SectionEditDialog>
  );
};

/** Name, location code (read-only), specialty, practice type and operatories. */
export const BasicInformationModal = (props: ISectionModalProps) => (
  <RequireCredentials
    title={TITLE}
    dataCy={DATA_CY}
    credentials={props.credentials}
    onClose={props.onClose}
  >
    {(credentials) => <BasicInformationForm {...props} credentials={credentials} />}
  </RequireCredentials>
);
